import _, { parseInt } from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import ProjectsPaginatedGql from '@/extensions/tickets/store/graphql/queries/ProjectsPaginated.gql';
import ProjectTicketsPaginatedGql from '@/extensions/tickets/store/graphql/queries/ProjectTicketsPaginated.gql';
import ProjectGql from '@/extensions/tickets/store/graphql/queries/Project.gql';
import ProjectOptionsGql from '@/extensions/tickets/store/graphql/queries/Projects.gql';
import CreateProjectGql from '@/extensions/tickets/store/graphql/mutations/CreateProject.gql';
import UpdateProjectGql from '@/extensions/tickets/store/graphql/mutations/UpdateProject.gql';
import DeleteProjectGql from '@/extensions/tickets/store/graphql/mutations/DeleteProject.gql';

// The associations are object inputs mirroring the output type, so their id is
// the thing that needs coercing - the same boundary rule api/ticket.js applies,
// and for the same reason: one uncoerced id fails the whole mutation.
const OBJECT_ID_FIELDS = ['cms', 'lead'];

const cleaned = (input) => {
    const payload = _.removeDeepByKey(_.cloneDeep(input), '__typename');

    OBJECT_ID_FIELDS.forEach((field) => {
        const id = _.get(payload, `${field}.id`);
        if (id === null || id === undefined || id === '') {
            if (_.has(payload, field)) payload[field] = null;
            return;
        }
        payload[field] = { id: parseInt(id, 10) };
    });

    // The full list the drawer edits (the server replaces the set); a row
    // added in the drawer has no id yet.
    if (_.has(payload, 'milestones')) {
        payload.milestones = (payload.milestones || []).map((milestone) =>
            _.omitBy({ id: milestone.id ? parseInt(milestone.id, 10) : null, name: milestone.name, date: milestone.date }, _.isNil)
        );
    }

    // Derived on the server and mirrored in the input only so the client can
    // post the record back whole; sending them back is noise.
    return _.omit(payload, [
        'tickets',
        'tickets_count',
        'open_tickets_count',
        'closed_tickets_count',
        'progress',
        'support_minutes',
        'billable_support_minutes',
        'created_at',
        'updated_at',
    ]);
};

export default {
    fetchPaginated: (state, desk, cmsId) =>
        new Promise((resolve, reject) => {
            const filters = [];
            _.each(state.filters, (entry) => filters.push(entry));

            apolloClient
                .query({
                    query: ProjectsPaginatedGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        desk: desk || undefined,
                        // The shop being administered - a super user must name
                        // it, a shop's own user is scoped by their rights.
                        cms_id: cmsId ? parseInt(cmsId) : undefined,
                        currentPage: _.get(state, 'pageInfo.currentPage', 1),
                        perPage: _.get(state, 'pageInfo.perPage', 25),
                        order: _.get(state, 'variables.order', 'name'),
                        direction: _.get(state, 'variables.direction', 'asc'),
                        link: _.get(state, 'link.route', ''),
                        filters,
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_ProjectsPaginated))
                .catch((err) => reject(err));
        }),

    fetch: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({ query: ProjectGql, variables: { id: parseInt(id) }, fetchPolicy: 'network-only' })
                .then(({ data }) => resolve(data.EX_TCKT_Project))
                .catch((err) => reject(err));
        }),

    // Which projects a ticket may be filed under: this customer's plus every
    // cross-customer one of the desk.
    fetchTicketsPaginated: ({ desk, projectId, currentPage = 1, perPage = 10 } = {}) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: ProjectTicketsPaginatedGql,
                    fetchPolicy: 'network-only',
                    variables: {
                        desk: desk || undefined,
                        currentPage,
                        perPage,
                        filters: [{ key: 'project', in: [String(projectId)] }],
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketsPaginated))
                .catch((err) => reject(err));
        }),

    /**
     * `allCustomers` drops the cms_id argument entirely. The query reads its
     * mere PRESENCE as "what a ticket of this customer may join", so sending
     * null narrows the list to cross-customer projects - which is right for the
     * ticket form and wrong for a picker that watches the whole desk.
     */
    fetchOptions: ({ desk, cmsId, like, includeArchived, allCustomers } = {}) =>
        new Promise((resolve, reject) => {
            const variables = {
                desk: desk || undefined,
                like: like || undefined,
                include_archived: includeArchived || undefined,
            };

            if (!allCustomers) variables.cms_id = cmsId ? parseInt(cmsId, 10) : null;

            apolloClient
                .query({
                    query: ProjectOptionsGql,
                    fetchPolicy: 'network-only',
                    variables,
                })
                .then(({ data }) => resolve(data.EX_TCKT_Projects || []))
                .catch((err) => reject(err));
        }),

    create: (project) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: CreateProjectGql, variables: { project: cleaned(project) } })
                .then(({ data }) => resolve(data.EX_TCKT_CreateProject))
                .catch((err) => reject(err));
        }),

    update: (id, project) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: UpdateProjectGql, variables: { id: parseInt(id), project: cleaned(project) } })
                .then(({ data }) => resolve(data.EX_TCKT_UpdateProject))
                .catch((err) => reject(err));
        }),

    delete: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: DeleteProjectGql, variables: { id: parseInt(id) } })
                .then(({ data }) => resolve(data.EX_TCKT_DeleteProject))
                .catch((err) => reject(err));
        }),
};
